import React from 'react';
import {
  Box, Container, Typography, Chip, Button, Stack, useTheme,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GitHubIcon from '@mui/icons-material/GitHub';
import AppleIcon from '@mui/icons-material/Apple';
import { motion } from 'motion/react';
import { content, ProjectItem } from '../../data/content';
import { useAnimationVariants } from '../../hooks/useAnimationVariants';
import { useTiltEffect } from '../../hooks/useTiltEffect';

const MotionBox = motion.create(Box);

// SVG icons
const MobileIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
  </svg>
);
const WebIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);

const TechChips: React.FC<{ tech: string[] }> = ({ tech }) => (
  <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
    {tech.map((t) => (
      <Chip key={t} label={t} size="small" variant="outlined"
        sx={{ fontSize: '0.6875rem', height: 24, borderRadius: '5px' }} />
    ))}
  </Stack>
);

const AppStoreBadge: React.FC<{ href: string }> = ({ href }) => {
  const theme = useTheme();
  return (
    <Box
      component="a" href={href} target="_blank" rel="noopener noreferrer"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.75,
        px: 1.25, py: 0.625, borderRadius: '7px',
        border: `1px solid ${theme.palette.text.primary}30`,
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
        textDecoration: 'none',
        transition: 'border-color 220ms ease, background-color 220ms ease',
        '&:hover': { borderColor: theme.palette.primary.main, bgcolor: `${theme.palette.primary.main}10` },
      }}
    >
      <AppleIcon sx={{ fontSize: '0.875rem', color: 'text.primary' }} />
      <Box>
        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.45rem', color: 'text.secondary', lineHeight: 1, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Download on the
        </Typography>
        <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: '0.6875rem', color: 'text.primary', lineHeight: 1 }}>
          App Store
        </Typography>
      </Box>
    </Box>
  );
};

// Project logo — 'cover' fills the frame, 'contain' shows the full logo on its brand color
const ProjectLogo: React.FC<{ project: ProjectItem; sx?: object }> = ({ project, sx }) => {
  if (!project.image) return null;
  const contain = project.imageFit === 'contain';
  return (
    <Box sx={{ overflow: 'hidden', bgcolor: project.imageBg ?? `${project.color}22`, ...sx }}>
      <Box
        component="img"
        src={project.image}
        alt={`${project.title} logo`}
        loading="lazy"
        sx={{ width: '100%', height: '100%', display: 'block', objectFit: contain ? 'contain' : 'cover', p: contain ? 2.5 : 0 }}
      />
    </Box>
  );
};

// ── Featured card — compact horizontal layout ────────────────────────────────
const FeaturedCard: React.FC<{ project: ProjectItem }> = ({ project }) => {
  const tilt = useTiltEffect({ maxRotation: 4 });
  const { featuredCard } = useAnimationVariants();

  return (
    <MotionBox
      variants={featuredCard}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-60px' }}
      sx={{ mb: { xs: 2.5, md: 3 } }}
    >
      <Box
        ref={tilt.ref}
        onMouseMove={tilt.handleMouseMove}
        onMouseLeave={tilt.handleMouseLeave}
        component={motion.div}
        style={{ rotateX: tilt.rotateX, rotateY: tilt.rotateY, transformStyle: 'preserve-3d' } as any}
        sx={{ perspective: '1200px' }}
      >
        <Box
          sx={{
            borderRadius: '18px',
            border: `1px solid ${project.color}35`,
            bgcolor: 'background.paper',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            position: 'relative',
            transition: 'border-color 280ms ease, box-shadow 280ms ease',
            '&:hover': {
              borderColor: `${project.color}55`,
              boxShadow: `0 0 40px ${project.color}18, 0 8px 32px rgba(0,0,0,0.25)`,
            },
          }}
        >
          {/* Left accent strip */}
          <Box
            sx={{
              width: { xs: '100%', md: '28%' },
              minHeight: { xs: 120, md: 'auto' },
              background: `linear-gradient(160deg, ${project.color}25 0%, ${project.color}0A 100%)`,
              borderRight: { md: `1px solid ${project.color}20` },
              borderBottom: { xs: `1px solid ${project.color}20`, md: 'none' },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              p: { xs: 2.5, md: 3 },
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {project.image ? (
              <ProjectLogo project={project} sx={{ width: { xs: 120, md: 150 }, aspectRatio: '1 / 1', borderRadius: '18px', zIndex: 1, mb: 0.5, boxShadow: `0 12px 32px ${project.color}30` }} />
            ) : (
              /* Faint icon watermark */
              <Box sx={{ position: 'absolute', opacity: 0.06, color: project.color, transform: 'scale(5)' }}>
                {project.category === 'mobile' ? <MobileIcon /> : <WebIcon />}
              </Box>
            )}
            <Chip
              label={project.status} size="small"
              sx={{ height: 24, fontSize: '0.6875rem', fontFamily: '"JetBrains Mono", monospace', bgcolor: `${project.color}22`, color: project.color, border: `1px solid ${project.color}50`, borderRadius: '5px', zIndex: 1 }}
            />
            <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.5625rem', letterSpacing: '0.14em', color: project.color, opacity: 0.65, zIndex: 1, textAlign: 'center' }}>
              FEATURED PROJECT
            </Typography>
            {project.appStoreLink && (
              <Box sx={{ zIndex: 1 }}>
                <AppStoreBadge href={project.appStoreLink} />
              </Box>
            )}
          </Box>

          {/* Right: Content */}
          <Box sx={{ flex: 1, p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.5625rem', letterSpacing: '0.14em', color: project.color, mb: 1.25, display: 'block', textTransform: 'uppercase' }}>
              {project.category === 'mobile' ? 'iOS · Android' : 'Web Application'}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1, lineHeight: 1.25 }}>
              {project.title}
            </Typography>
            <Typography variant="body2" sx={{ color: project.color, fontWeight: 600, mb: 1.75, fontSize: '0.9375rem' }}>
              {project.tagline}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2.5 }}>
              {project.description}
            </Typography>
            <TechChips tech={project.tech} />
            {!project.demo && !project.github && (
              <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: '"JetBrains Mono", monospace', opacity: 0.55, mt: 1.75, display: 'block' }}>
                Production · Private Repository
              </Typography>
            )}
            {project.demo && (
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" size="small" href={project.demo} target="_blank" rel="noopener noreferrer" endIcon={<OpenInNewIcon sx={{ fontSize: '0.8rem !important' }} />}
                  sx={{ background: `linear-gradient(135deg, ${project.color}, ${project.color}bb)`, color: '#fff', boxShadow: `0 0 20px ${project.color}40`, fontSize: '0.8125rem' }}>
                  View Live
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </MotionBox>
  );
};

// ── Medium / standard project card — no header, just colored top bar ─────────
const ProjectCard: React.FC<{ project: ProjectItem }> = ({ project }) => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tilt   = useTiltEffect({ maxRotation: 8 });
  const { card } = useAnimationVariants();

  const isClickable = !!project.demo;

  return (
    <MotionBox
      variants={card}
      whileHover={{ y: -6, scale: 1.005 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      sx={{ height: '100%' }}
    >
      <Box
        ref={tilt.ref}
        onMouseMove={tilt.handleMouseMove}
        onMouseLeave={tilt.handleMouseLeave}
        component={motion.div}
        style={{ rotateX: tilt.rotateX, rotateY: tilt.rotateY, transformStyle: 'preserve-3d', height: '100%' } as any}
        sx={{ perspective: '1000px' }}
      >
        <Box
          onClick={isClickable ? () => window.open(project.demo!, '_blank') : undefined}
          sx={{
            height: '100%', display: 'flex', flexDirection: 'column',
            borderRadius: '16px',
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper', overflow: 'hidden',
            cursor: isClickable ? 'pointer' : 'default',
            position: 'relative',
            transition: 'border-color 260ms ease, box-shadow 260ms ease',
            // Colored top accent bar
            '&::before': {
              content: '""', position: 'absolute', top: 0, left: 0, right: 0,
              height: '3px',
              background: `linear-gradient(90deg, ${project.color}, ${project.color}70)`,
              borderRadius: '16px 16px 0 0',
            },
            '&:hover': {
              borderColor: `${project.color}55`,
              boxShadow: `0 0 28px ${project.color}20, 0 8px 24px rgba(0,0,0,0.2)`,
            },
          }}
        >
          {/* Mouse-follow spotlight */}
          <Box
            component={motion.div}
            style={{ background: `radial-gradient(circle at ${tilt.glowX}% ${tilt.glowY}%, ${project.color}10, transparent 55%)` } as any}
            sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}
          />

          {/* Logo banner */}
          <ProjectLogo project={project} sx={{ height: 170, borderBottom: `1px solid ${theme.palette.divider}` }} />

          {/* Content area */}
          <Box sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1, pt: 3.5 }}>
            {/* Top row: category icon + badges */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ color: project.color, opacity: 0.7, display: 'flex' }}>
                  {project.category === 'mobile' ? <MobileIcon /> : <WebIcon />}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                <Chip label={project.status} size="small" sx={{ height: 22, fontSize: '0.625rem', fontFamily: '"JetBrains Mono", monospace', bgcolor: `${project.color}18`, color: project.color, border: `1px solid ${project.color}44`, borderRadius: '4px' }} />
                <Chip label={project.category === 'mobile' ? 'Mobile' : 'Web'} size="small" sx={{ height: 22, fontSize: '0.625rem', fontFamily: '"JetBrains Mono", monospace', bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: 'text.secondary', border: `1px solid ${theme.palette.divider}`, borderRadius: '4px' }} />
              </Box>
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5, lineHeight: 1.3, fontSize: '1rem' }}>
              {project.title}
            </Typography>
            <Typography variant="body2" sx={{ color: project.color, mb: 1.5, fontSize: '0.8125rem', fontWeight: 500 }}>
              {project.tagline}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.75, mb: 2, flexGrow: 1, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: '0.875rem' }}>
              {project.description}
            </Typography>
            <TechChips tech={project.tech} />
            <Box sx={{ mt: 1.75 }}>
              {project.appStoreLink && !project.demo && (
                <AppStoreBadge href={project.appStoreLink} />
              )}
              {!project.demo && !project.github && !project.appStoreLink && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: '"JetBrains Mono", monospace', opacity: 0.5, fontSize: '0.6875rem' }}>
                  Production · Private Repo
                </Typography>
              )}
              {(project.demo || project.github) && (
                <Stack direction="row" spacing={1}>
                  {project.demo && (
                    <Button size="small" variant="text" href={project.demo} target="_blank" rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon sx={{ fontSize: '0.75rem !important' }} />}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ px: 0, fontSize: '0.8125rem', fontWeight: 500 }}>
                      Live Demo
                    </Button>
                  )}
                  {project.github && (
                    <Button size="small" variant="text" href={project.github} target="_blank" rel="noopener noreferrer"
                      startIcon={<GitHubIcon sx={{ fontSize: '0.75rem !important' }} />}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ px: 0, fontSize: '0.8125rem', fontWeight: 500 }}>
                      Source
                    </Button>
                  )}
                </Stack>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </MotionBox>
  );
};

// ── Analytics landscape card ─────────────────────────────────────────────────
const LandscapeCard: React.FC<{ project: ProjectItem }> = ({ project }) => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tilt   = useTiltEffect({ maxRotation: 3 });
  const { card } = useAnimationVariants();

  const BAR_HEIGHTS = [35, 60, 48, 80, 55, 72, 42, 65, 50, 75];

  return (
    <MotionBox
      variants={card}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-60px' }}
      sx={{ mb: { xs: 2.5, md: 3 } }}
    >
      <Box
        ref={tilt.ref}
        onMouseMove={tilt.handleMouseMove}
        onMouseLeave={tilt.handleMouseLeave}
        component={motion.div}
        style={{ rotateX: tilt.rotateX, rotateY: tilt.rotateY, transformStyle: 'preserve-3d' } as any}
        sx={{ perspective: '1200px' }}
      >
        <Box
          sx={{
            borderRadius: '16px',
            border: `1px solid ${project.color}28`,
            bgcolor: 'background.paper', overflow: 'hidden',
            display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch',
            position: 'relative',
            transition: 'border-color 260ms ease, box-shadow 260ms ease',
            '&::before': {
              content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
              background: `linear-gradient(90deg, ${project.color}, ${project.color}60)`,
            },
            '&:hover': {
              borderColor: `${project.color}50`,
              boxShadow: `0 0 32px ${project.color}15, 0 6px 20px rgba(0,0,0,0.18)`,
            },
          }}
        >
          {/* Content */}
          <Box sx={{ flex: 1, p: { xs: 3, md: 3.5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
              <Chip label={project.status} size="small" sx={{ height: 22, fontSize: '0.625rem', fontFamily: '"JetBrains Mono", monospace', bgcolor: `${project.color}18`, color: project.color, border: `1px solid ${project.color}44`, borderRadius: '4px' }} />
              <Chip label="Web" size="small" sx={{ height: 22, fontSize: '0.625rem', fontFamily: '"JetBrains Mono", monospace', bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: 'text.secondary', border: `1px solid ${theme.palette.divider}`, borderRadius: '4px' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5, fontSize: '1.125rem' }}>
              {project.title}
            </Typography>
            <Typography variant="body2" sx={{ color: project.color, fontWeight: 600, mb: 1.25, fontSize: '0.875rem' }}>
              {project.tagline}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.75, mb: 2, fontSize: '0.875rem' }}>
              {project.description}
            </Typography>
            <TechChips tech={project.tech} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: '"JetBrains Mono", monospace', opacity: 0.5, mt: 1.25, display: 'block', fontSize: '0.6875rem' }}>
              Production · Private Repository
            </Typography>
          </Box>

          {/* Logo, or bar chart visual when there's no logo */}
          {project.image ? (
            <ProjectLogo project={project} sx={{ width: { xs: '100%', md: 260 }, height: { xs: 180, md: 'auto' }, flexShrink: 0, borderLeft: { md: `1px solid ${project.color}20` } }} />
          ) : (
          <Box sx={{ width: { xs: '100%', md: 220 }, height: { xs: 100, md: 'auto' }, background: `linear-gradient(135deg, ${project.color}12 0%, ${project.color}05 100%)`, borderLeft: { md: `1px solid ${project.color}20` }, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', px: 2.5, pb: 2.5, pt: { xs: 2.5, md: 0 }, gap: 0.75 }}>
            {BAR_HEIGHTS.map((h, i) => (
              <MotionBox
                key={i}
                initial={{ scaleY: 0 }}
                whileInView={{ scaleY: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                sx={{ width: 10, height: h, borderRadius: '3px 3px 0 0', background: `linear-gradient(to top, ${project.color}85, ${project.color}35)`, transformOrigin: 'bottom', flexShrink: 0 }}
              />
            ))}
          </Box>
          )}
        </Box>
      </Box>
    </MotionBox>
  );
};

// ── Main Projects section ────────────────────────────────────────────────────
const Projects: React.FC = () => {
  const { overline, title, items } = content.projects;
  const { fadeUp, stagger } = useAnimationVariants();

  const featured  = items.find((p) => p.id === 1)!;
  const row2      = items.filter((p) => p.id === 2 || p.id === 3);
  const landscape = items.find((p) => p.id === 4)!;
  const row4      = items.filter((p) => p.id === 5 || p.id === 6);
  const rest      = items.filter((p) => p.id > 6);

  return (
    <Box id="projects" sx={{ py: { xs: 10, md: 16 }, bgcolor: 'background.paper', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', top: '-10%', left: '-8%', width: '40%', paddingBottom: '40%', borderRadius: '50%', background: 'radial-gradient(circle, rgba(167,139,250,0.1) 0%, transparent 70%)', pointerEvents: 'none', filter: 'blur(60px)', zIndex: 0 }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <MotionBox variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }} sx={{ mb: { xs: 7, md: 10 } }}>
          <MotionBox variants={fadeUp}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', letterSpacing: '0.14em', mb: 1.5, display: 'block' }}>
              {overline}
            </Typography>
          </MotionBox>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <MotionBox variants={fadeUp}>
              <Typography variant="h2" component="h2" sx={{ color: 'text.primary', fontWeight: 700 }}>
                {title}
              </Typography>
            </MotionBox>
            <MotionBox variants={fadeUp}>
              <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontSize: { xs: '2rem', md: '4rem' }, fontWeight: 800, color: 'primary.main', opacity: 0.07, lineHeight: 1, userSelect: 'none', display: { xs: 'none', md: 'block' } }}>
                {String(items.length).padStart(2, '0')} Projects
              </Typography>
            </MotionBox>
          </Box>
        </MotionBox>

        {/* Row 1: Featured */}
        <FeaturedCard project={featured} />

        {/* Row 2 */}
        <MotionBox variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }}
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2.5, md: 3 }, mb: { xs: 2.5, md: 3 } }}>
          {row2.map((p) => <ProjectCard key={p.id} project={p} />)}
        </MotionBox>

        {/* Row 3: Landscape */}
        <LandscapeCard project={landscape} />

        {/* Row 4 */}
        <MotionBox variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }}
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2.5, md: 3 } }}>
          {row4.map((p) => <ProjectCard key={p.id} project={p} />)}
        </MotionBox>

        {/* Any further projects continue in the same 2-col grid */}
        {rest.length > 0 && (
          <MotionBox variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }}
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2.5, md: 3 }, mt: { xs: 2.5, md: 3 } }}>
            {rest.map((p) => <ProjectCard key={p.id} project={p} />)}
          </MotionBox>
        )}
      </Container>
    </Box>
  );
};

export default Projects;
